import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api, resolveImg } from "@/lib/api";
import { Reveal } from "./Reveal";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { EditableText } from "@/components/site/edit/EditableText";

export function Categories() {
  const navigate = useNavigate();
  const { t, tl } = useLang();
  const { data: allCategories = [] } = useQuery({
    queryKey: ["categories"],
    queryFn: async () => (await api.get("/categories")).data,
  });
  const categories = allCategories.filter((c) => !c.parent_id && !c.is_service && !c.hidden);

  return (
    <section id="programs" data-testid="programs-section" className="relative z-10 py-20 sm:py-28 bg-[#F1EEE8]">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <Reveal>
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#BE923C]"><EditableText k="programs.eyebrow" as="span" /></span>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight max-w-xl leading-tight">
              <EditableText k="programs.title" as="span" />
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="max-w-sm text-[#475569]">
              <EditableText k="programs.desc" as="span" multiline />
            </p>
          </Reveal>
        </div>

        <div className="grid grid-cols-12 gap-5">
          {categories.map((cat, i) => (
            <Reveal key={cat.id} delay={(i % 3) * 0.06} className={cat.span}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ type: "spring", stiffness: 300, damping: 24 }}
                onClick={() => navigate(cat.link || `/programs/${cat.slug}`)}
                data-testid={`category-card-${cat.slug}`}
                className="group relative h-72 sm:h-80 rounded-3xl overflow-hidden cursor-pointer shadow-[0_8px_30px_rgba(15,23,42,0.06)]"
              >
                <img
                  src={resolveImg(cat.image_url)}
                  alt={tl(cat, "title")}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/85 via-[#0F172A]/25 to-transparent" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-serif text-2xl sm:text-[1.75rem] leading-tight">{tl(cat, "title")}</h3>
                    <span className="shrink-0 w-10 h-10 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center opacity-0 -translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-300">
                      <ArrowUpRight size={18} />
                    </span>
                  </div>
                  <p className="mt-3 text-sm text-white/85 max-w-md leading-relaxed line-clamp-3">
                    {tl(cat, "description")}
                  </p>
                </div>
              </motion.div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
